import React, { useState, useEffect } from "react";
import { Circle, Info } from "lucide-react";
import { checkCameraActive } from "../../api/camStatus.api";
import { getLatestImage } from "../../api/lastImage.api";
import { useObraTimeZone } from "../../../obras/hooks/useObraTimeZone";

const formatLastImageDate = (dateStr, timeZone) => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;

  return date.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: timeZone || undefined,
  });
};

const CamStatus = ({ feedID }) => {
  const obraTimeZone = useObraTimeZone(feedID);
  const [isActive, setIsActive] = useState(undefined);
  const [lastImageDate, setLastImageDate] = useState(undefined);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    if (!feedID) return;

    let isMounted = true;
    checkCameraActive(feedID).then((active) => {
      if (isMounted) setIsActive(active);
    });

    return () => {
      isMounted = false;
    };
  }, [feedID]);

  useEffect(() => {
    if (!feedID || isActive === undefined) return;

    let isMounted = true;
    getLatestImage(feedID)
      .then((response) => {
        const date = typeof response === "object" ? response?.date : null;
        if (isMounted) setLastImageDate(date || null);
      })
      .catch(() => {
        if (isMounted) setLastImageDate(null);
      });

    return () => {
      isMounted = false;
    };
  }, [feedID, isActive]);

  if (isActive === undefined) {
    return <span className="text-xs text-gray-500 dark:text-gray-400">Cargando...</span>;
  }

  const formattedDate = lastImageDate ? formatLastImageDate(lastImageDate, obraTimeZone) : null;

  return (
    <div className="relative flex items-center justify-center gap-2">
      <Circle className={`w-3 h-3 ${isActive ? "fill-green-500 text-green-500" : "fill-red-500 text-red-500"}`} />
      <span className={`text-xs font-semibold ${isActive ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
        {isActive ? "Transmitiendo" : "Sin transmisión"}
      </span>

      {lastImageDate !== undefined && (
        <button
          type="button"
          title="Última imagen"
          onClick={() => setShowInfo((prev) => !prev)}
          className="p-1 [@media(pointer:coarse)]:p-4 [@media(pointer:coarse)]:-m-3.5 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      )}

      {showInfo && lastImageDate !== undefined && (
        <div className="absolute z-50 bottom-full mb-1 left-1/2 -translate-x-1/2 w-56 p-2 rounded-lg shadow-lg bg-gray-800 text-white text-xs">
          {formattedDate
            ? `La última foto fue tomada el: ${formattedDate}`
            : "No tenemos registros de imágenes en los últimos 90 días"}
        </div>
      )}
    </div>
  );
};

export default CamStatus;
